import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { useMainMachine } from '../../../utils/useMainMachine';
import { DisclosureButton } from '../basic-ui/DisclosureButton';
import { PulsatingDot } from '../basic-ui/PulsatingDot';
import { inflect } from '../../../utils/string';
import { ACTIONS } from '../../../state';
import { getSegmentAsText } from '../../../utils/segments';
import { formatEditorDate } from '../../../utils/date';
import { goodContrastWithWhite } from '../../../utils/utils';

import TickIcon from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg?react';
import ImageIcon from '@crazyegginc/hatch/dist/images/icon-image.svg?react';
import VersionsIcon from '@crazyegginc/hatch/dist/images/icon-ab-test-outline.svg?react';
import { GENERATING } from '../../constants';

export function Versions() {
  const { useSelector, machine } = useMainMachine();
  const versions = useSelector(
    ({ context }) => context.editor.pages.find((p) => p.id === context.activePageId)?.versions ?? [],
  );
  const pageThumbnail = useSelector(
    ({ context }) => context.editor.pages.find((p) => p.id === context.activePageId)?.thumbnailUrl,
  );
  const open = useSelector(({ context }) => context.versionsOpen);
  const timeZone = useSelector(({ context }) => context.timeZone);

  const liveEveryoneVersion = versions.find((v) => v.published_at != null && v.segment == null);

  let versionsWithLive;
  if (!liveEveryoneVersion) {
    const liveEveryoneVersion = {
      id: null,
      published_at: true,
      thumbnailUrl: pageThumbnail,
      segment: null,
    };
    const insertIndex = versions.findIndex((v) => v.published_at == null);
    versionsWithLive =
      insertIndex !== -1
        ? [...versions.slice(0, insertIndex), liveEveryoneVersion, ...versions.slice(insertIndex)]
        : [...versions, liveEveryoneVersion];
  } else {
    versionsWithLive = versions;
  }

  return (
    <div className="flex flex-col mt-5" data-testid="versions">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <VersionsIcon className="w-4 h-4 fill-current mr-2.5" />
          <span className="font-semibold">Versions</span>
          <span className="ml-2.5 text-gray-200">
            {versions.length} {inflect('version', versions.length)}
          </span>
        </div>

        <DisclosureButton
          open={open}
          onClick={() => machine.send({ type: ACTIONS.TOGGLE_VERSIONS })}
          contentId="versions-content"
        />
      </div>
      <div className="overflow-hidden">
        <motion.div
          initial={false}
          animate={
            open
              ? {
                  height: 'auto',
                  opacity: 1,
                }
              : {
                  height: 0,
                  opacity: 0,
                }
          }
          transition={{ duration: 0.4, ease: 'linear' }}
          className="mt-2.5 flex flex-col"
          id="versions-content"
        >
          <motion.div
            animate={open ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.2, ease: 'linear' }}
            data-testid="versions-list"
          >
            {versionsWithLive.map((v) => (
              <Version
                key={v.id}
                id={v.id}
                live={v.published_at != null}
                thumb={v.thumbnailUrl}
                updatedAt={v.updated_at}
                name={v.name}
                voiceName={v.metadata?.voice}
                segment={v.segment}
                timeZone={timeZone}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

function Version({ id, live, thumb, updatedAt, name, voiceName, segment, timeZone }) {
  const { useSelector, machine } = useMainMachine();
  // Voice can be null, so ensure voice is used safely throughout the component
  const voice = useSelector(({ context }) => context.brandGuidelines.voices.find((v) => v.name === voiceName));
  const active = useSelector(({ context }) => context.activeVersionId == id);
  const generating = useSelector((state) => state.hasTag(GENERATING));
  const [hasError, setHasError] = useState(!thumb);
  const [open, setOpen] = useState(false);

  const displayDate = formatEditorDate(updatedAt, timeZone);

  // if thumb changes, reset error
  useEffect(() => {
    if (thumb) {
      setHasError(false);
    }
  }, [thumb]);

  const segmentText = getSegmentAsText(segment);

  return (
    <motion.div layout transition={{ duration: 1 }} className="mt-5 first:mt-0" data-testid="version">
      <button
        type="button"
        style={{ '--selectedvoice': voice?.color ?? '' }}
        className="text-left relative group overflow-hidden rounded-md disabled:cursor-not-allowed"
        onClick={() => machine.send({ type: ACTIONS.SELECT_VERSION, versionId: id })}
        disabled={generating}
      >
        {hasError ? (
          <div className="absolute w-[230px] !max-w-[230px] h-[160px] rounded-md flex items-center justify-center bg-gray-500">
            <ImageIcon className="w-11 h-11 fill-current text-gray-900" />
          </div>
        ) : (
          <img
            src={thumb}
            alt="version thumbnail"
            className={classNames('absolute w-[230px] !max-w-[230px] h-[160px] object-contain rounded-md bg-gray-500')}
            onError={() => setHasError(true)}
          />
        )}
        <div
          id="overlay"
          className={classNames(
            'absolute w-[230px] h-[160px]',
            live && 'bg-transparent',
            !live &&
              !voice && {
                'group-hover:bg-blue-300/20': true,
                'bg-blue-300/20': active,
              },
            !live &&
              voice && {
                'group-hover:bg-selectedvoice/20': true,
                'bg-selectedvoice/20': active,
              },
          )}
        />
        <div
          id="inside-border"
          className={classNames(
            'relative w-[230px] h-[160px] rounded-md flex items-center justify-center border-[3px] mb-2.5',
            !active && 'border-transparent',
            live && {
              'group-hover:border-green-500': true,
              'border-green-500': active,
            },
            !live &&
              !voice && {
                'group-hover:border-blue-300': true,
                'border-blue-300': active,
              },
            !live &&
              voice && {
                'group-hover:border-selectedvoice': true,
                'border-selectedvoice': active,
              },
          )}
        >
          {active ? (
            <>
              <div
                className={classNames(
                  'absolute top-0 left-0 w-11 h-11 rotate-45 -translate-x-1/2 -translate-y-1/2',
                  live
                    ? 'bg-green-500'
                    : {
                        'bg-blue-300': !voice,
                        'bg-selectedvoice': voice,
                      },
                )}
              />
              <TickIcon
                className={classNames('absolute h-3 w-3 fill-current top-1 left-0.5', {
                  'text-white': !voice || goodContrastWithWhite(voice?.color) || live,
                  'text-gray-900': voice && !goodContrastWithWhite(voice?.color) && !live,
                })}
                aria-label="active version"
              />
            </>
          ) : null}
        </div>
        {live ? (
          <div className="flex items-center space-x-1.25 text-xs">
            <PulsatingDot />
            <span>Live</span>
          </div>
        ) : null}
        <div className="flex items-center space-x-1.25 text-xs">
          {name ? (
            <span
              className={classNames({
                'text-white': !voice,
                'text-selectedvoice': voice,
              })}
            >
              {name}
            </span>
          ) : null}
          {updatedAt ? (
            <>
              <div className="w-[3px] h-[3px] rounded-full bg-gray-200 mx-1.25" />
              <span className="text-gray-200">{displayDate}</span>
            </>
          ) : null}
        </div>
      </button>
      {
        <div className="flex items-center justify-between text-xs">
          <div>{segmentText.name}</div>
          {segmentText.details ? (
            <DisclosureButton open={open} onClick={() => setOpen(!open)} contentId="segment-content" />
          ) : null}
        </div>
      }
      {segmentText.details ? (
        <div className="overflow-hidden">
          <motion.div
            initial={false}
            animate={
              open
                ? {
                    height: 'auto',
                    opacity: 1,
                  }
                : {
                    height: 0,
                    opacity: 0,
                  }
            }
            transition={{ duration: 0.2, ease: 'linear' }}
            className={classNames('leading-tight text-xs text-gray-200', {
              'whitespace-pre truncate ': segmentText.name === 'Referrer',
              'whitespace-pre-wrap': segmentText.name !== 'Referrer',
            })}
            id="segment-content"
            data-testid="segment-content"
          >
            {segmentText.details}
          </motion.div>
        </div>
      ) : null}
    </motion.div>
  );
}
